import emailjs from "@emailjs/browser";
import { useState } from "react";
import {
  FaEnvelope,
  FaPhoneAlt,
  FaRegCommentDots,
  FaUser,
} from "react-icons/fa";
import { Link } from "react-router-dom";
import { contactInfo } from "../../data/contactInfo";
import RenderToast from "../RenderToast";
import styles from "./ContactForm.module.css";

const initialValues = {
  name: "",
  email: "",
  phone: "",
  message: "",
  consent: false,
};

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const emailJsConfig = {
  serviceId: process.env.REACT_APP_EMAILJS_SERVICE_ID,
  templateId: process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
  publicKey: process.env.REACT_APP_EMAILJS_PUBLIC_KEY,
};

export default function ContactForm() {
  const [formValues, setFormValues] = useState(initialValues);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    setFormValues((currentValues) => ({
      ...currentValues,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const trimmedValues = {
      name: formValues.name.trim(),
      email: formValues.email.trim(),
      phone: formValues.phone.trim(),
      message: formValues.message.trim(),
      consent: formValues.consent,
    };

    if (!trimmedValues.name) {
      RenderToast({
        type: "error",
        message: "Please enter your name. It is required.",
      });
      return;
    }

    if (!trimmedValues.email) {
      RenderToast({
        type: "error",
        message: "Please enter your email. It is required.",
      });
      return;
    }

    if (!trimmedValues.phone) {
      RenderToast({
        type: "error",
        message: "Please enter your phone number. It is required.",
      });
      return;
    }

    if (!trimmedValues.consent) {
      RenderToast({
        type: "error",
        message: "Please accept the consent checkbox to continue.",
      });
      return;
    }

    if (!emailPattern.test(trimmedValues.email)) {
      RenderToast({
        type: "error",
        message: "Please enter a valid email address.",
      });
      return;
    }

    if (
      !emailJsConfig.serviceId ||
      !emailJsConfig.templateId ||
      !emailJsConfig.publicKey
    ) {
      RenderToast({
        type: "error",
        message:
          "Email service is not configured yet. Add the EmailJS keys to your .env file.",
      });
      return;
    }

    try {
      setIsSubmitting(true);

      await emailjs.send(
        emailJsConfig.serviceId,
        emailJsConfig.templateId,
        {
          from_name: trimmedValues.name,
          from_email: trimmedValues.email,
          phone: trimmedValues.phone,
          message: trimmedValues.message || "No message provided.",
          consent: trimmedValues.consent ? "Yes" : "No",
        },
        {
          publicKey: emailJsConfig.publicKey,
        },
      );

      RenderToast({
        type: "success",
        message:
          "Your message has been submitted successfully. Our team will reach out soon.",
      });

      setFormValues(initialValues);
    } catch (error) {
      RenderToast({
        type: "error",
        message:
          "We could not send your message right now. Please try again in a moment.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className={styles.formCard} onSubmit={handleSubmit} noValidate>
      <div className={styles.fieldGrid}>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>Name *</span>
          <div className={styles.inputWrap}>
            <span className={styles.icon} aria-hidden="true">
              <FaUser />
            </span>
            <input
              type="text"
              name="name"
              value={formValues.name}
              onChange={handleChange}
              placeholder="Enter your full name"
              autoComplete="name"
            />
          </div>
        </label>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>Email *</span>
          <div className={styles.inputWrap}>
            <span className={styles.icon} aria-hidden="true">
              <FaEnvelope />
            </span>
            <input
              type="email"
              name="email"
              value={formValues.email}
              onChange={handleChange}
              placeholder="Enter your email address"
              autoComplete="email"
            />
          </div>
        </label>

        <label className={`${styles.field} ${styles.fieldPhone}`}>
          <span className={styles.fieldLabel}>Phone *</span>
          <div className={styles.inputWrap}>
            <span className={styles.icon} aria-hidden="true">
              <FaPhoneAlt />
            </span>
            <input
              type="tel"
              name="phone"
              value={formValues.phone}
              onChange={handleChange}
              placeholder="Enter your phone number"
              autoComplete="tel"
            />
          </div>
        </label>

        <label className={`${styles.field} ${styles.fieldFull}`}>
          <span className={styles.fieldLabel}>Message</span>
          <div className={`${styles.inputWrap} ${styles.textareaWrap}`}>
            <span className={styles.icon} aria-hidden="true">
              <FaRegCommentDots />
            </span>
            <textarea
              name="message"
              value={formValues.message}
              onChange={handleChange}
              placeholder="Tell us about your organization, current challenges, or goals."
              rows="2"
            />
          </div>
        </label>
      </div>

      <div className={styles.consent}>
        <input
          type="checkbox"
          name="consent"
          checked={formValues.consent}
          onChange={handleChange}
          id="contact-consent"
        />
        <label htmlFor="contact-consent" className={styles.consentText}>
          By opting in for text messages, you agree to receive an appointment
          reminders and important updates from <strong>PSP</strong> at the
          number provided. Message frequency varies. Msg &amp; data rates may
          apply. No mobile opt-in data will be shared with third parties. Reply{" "}
          <strong>HELP - </strong>
          <a
            href={contactInfo.phoneHref}
            onClick={(event) => event.stopPropagation()}
          >
            <strong>{contactInfo.phoneDisplay}</strong>
          </a>{" "}
          for help. Reply <strong>STOP</strong> to <strong>Unsubscribe</strong>.
          View our{" "}
          <Link
            to="/terms-and-conditions"
            onClick={(event) => event.stopPropagation()}
          >
            <strong>Terms &amp; Conditions</strong>
          </Link>{" "}
          and{" "}
          <Link
            to="/privacy-policy"
            onClick={(event) => event.stopPropagation()}
          >
            <strong>Privacy Policy</strong>
          </Link>{" "}
          for more help.
        </label>
      </div>

      <button type="submit" className={styles.submitButton}>
        {isSubmitting ? "Sending..." : "Send Message"}
      </button>
    </form>
  );
}
